import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { api } from "../api";

function EmployeeDrawingReport() {
  const [logs, setLogs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [viewMode, setViewMode] = useState("tillnow");
  const [selectedFY, setSelectedFY] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [empRates, setEmpRates] = useState({});
  const [loading, setLoading] = useState(false);

  const authHeader = {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  };

  const normalizeName = (name) =>
    String(name || "")
      .trim()
      .toLowerCase();

  const getUserName = (log) =>
    log.userName ||
    log.employeeName ||
    log.createdByName ||
    log.createdBy?.name ||
    log.user?.name ||
    log.employee?.name ||
    "Unknown User";

  const getLogHours = (log) => Number(log.actualHours || 0);

  const getLogRate = (log) => {
    return Number(log.hourlyRateAtEntry || 0);
  };

  const getLogCost = (log) => {
    const rate = getLogRate(log);
    return getLogHours(log) * rate;
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const [logsRes, projectsRes, usersRes] = await Promise.all([
        api.get("/logs/all", authHeader),
        api.get("/projects", authHeader),
        api.get("/users", authHeader),
      ]);

      const allLogs = Array.isArray(logsRes.data) ? logsRes.data : [];
      const allProjects = Array.isArray(projectsRes.data)
        ? projectsRes.data
        : [];
      const allUsers = Array.isArray(usersRes.data) ? usersRes.data : [];

      const rateMap = {};
      allUsers.forEach((emp) => {
        const key = normalizeName(emp.name);
        const rate = Number(emp.hourlyRate || 0);
        if (key) rateMap[key] = rate;
      });

      setLogs(allLogs);
      setProjects(allProjects);
      setEmpRates(rateMap);

      const firstProject =
        allProjects?.[0]?.name || allLogs?.[0]?.projectName || "";

      setSelectedProject(firstProject);
    } catch (error) {
      console.error("Employee drawing report fetch error:", error);
      alert(error.response?.data?.message || "Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDrawings = (log) => {
    if (Array.isArray(log.drawingDocuments)) return log.drawingDocuments;
    if (Array.isArray(log.drawingDocument)) return log.drawingDocument;

    return String(log.drawingDocument || "")
      .split(/\s*\|\s*|,\s*/g)
      .map((x) => x.trim())
      .filter(Boolean);
  };

  const getFY = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const startYear = month >= 4 ? year : year - 1;
    return `${startYear}-${startYear + 1}`;
  };

  const getMonthKey = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  const getMonthLabel = (val) => {
    if (!val) return "No Month";
    const [year, month] = val.split("-");
    return new Date(`${year}-${month}-01`).toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    });
  };

  const projectOptions = useMemo(() => {
    const fromProjects = projects.map((p) => p.name).filter(Boolean);
    const fromLogs = logs.map((l) => l.projectName).filter(Boolean);

    return [...new Set([...fromProjects, ...fromLogs])].sort((a, b) =>
      a.localeCompare(b),
    );
  }, [projects, logs]);

  const projectSelectOptions = useMemo(() => {
    return projectOptions.map((project) => ({
      value: project,
      label: project,
    }));
  }, [projectOptions]);

  const selectedProjectOption = useMemo(() => {
    return (
      projectSelectOptions.find((option) => option.value === selectedProject) ||
      null
    );
  }, [projectSelectOptions, selectedProject]);

  const fyOptions = useMemo(() => {
    return [...new Set(logs.filter((l) => l.date).map((l) => getFY(l.date)))]
      .sort()
      .reverse();
  }, [logs]);

  const monthOptions = useMemo(() => {
    return [
      ...new Set(logs.filter((l) => l.date).map((l) => getMonthKey(l.date))),
    ]
      .sort()
      .reverse();
  }, [logs]);

  useEffect(() => {
    if (fyOptions.length && !selectedFY) setSelectedFY(fyOptions[0]);
  }, [fyOptions, selectedFY]);

  useEffect(() => {
    if (monthOptions.length && !selectedMonth) {
      setSelectedMonth(monthOptions[0]);
    }
  }, [monthOptions, selectedMonth]);

  const selectedProjectLogs = useMemo(() => {
    return logs.filter((log) => {
      if (log.projectName !== selectedProject) return false;

      if (viewMode === "tillnow") return true;

      if (viewMode === "fy") {
        if (!log.date) return false;
        return getFY(log.date) === selectedFY;
      }

      if (viewMode === "month") {
        if (!log.date) return false;
        return getMonthKey(log.date) === selectedMonth;
      }

      return true;
    });
  }, [logs, selectedProject, viewMode, selectedFY, selectedMonth]);

  const employeeNames = useMemo(() => {
    return [
      ...new Set(
        selectedProjectLogs
          .map((log) => getUserName(log))
          .filter((name) => name && name !== "Unknown User"),
      ),
    ].sort((a, b) => a.localeCompare(b));
  }, [selectedProjectLogs]);

  const workTypeReport = useMemo(() => {
    const map = {};

    selectedProjectLogs.forEach((log) => {
      const workType = log.workType || "Unnamed Work Type";
      const employee = getUserName(log);
      const actual = getLogHours(log);
      const estimated = Number(log.estimatedHours || 0);
      const cost = getLogCost(log);
      const drawings = getDrawings(log);

      if (!map[workType]) {
        map[workType] = {
          workType,
          drawingSet: new Set(),
          totalDocs: 0,
          actualTotal: 0,
          estimatedTotal: 0,
          costTotal: 0,
          employees: {},
          drawings: {},
        };
      }

      map[workType].actualTotal += actual;
      map[workType].estimatedTotal += estimated;
      map[workType].costTotal += cost;
      map[workType].employees[employee] =
        (map[workType].employees[employee] || 0) + actual;

      drawings.forEach((doc) => {
        map[workType].drawingSet.add(doc);
        map[workType].totalDocs += 1;

        if (!map[workType].drawings[doc]) {
          map[workType].drawings[doc] = {
            name: doc,
            actualTotal: 0,
            estimatedTotal: 0,
            costTotal: 0,
            employees: {},
          };
        }

        map[workType].drawings[doc].actualTotal += actual;
        map[workType].drawings[doc].estimatedTotal += estimated;
        map[workType].drawings[doc].costTotal += cost;
        map[workType].drawings[doc].employees[employee] =
          (map[workType].drawings[doc].employees[employee] || 0) + actual;
      });
    });

    return Object.values(map)
      .map((item) => ({
        ...item,
        uniqueDrawingCount: item.drawingSet.size,
        drawings: Object.values(item.drawings).sort(
          (a, b) => b.actualTotal - a.actualTotal,
        ),
      }))
      .sort((a, b) => b.actualTotal - a.actualTotal);
  }, [selectedProjectLogs, empRates]);

  const totalActual = selectedProjectLogs.reduce(
    (s, l) => s + getLogHours(l),
    0,
  );

  const totalEstimated = selectedProjectLogs.reduce(
    (s, l) => s + Number(l.estimatedHours || 0),
    0,
  );

  const totalCost = selectedProjectLogs.reduce(
    (sum, log) => sum + getLogCost(log),
    0,
  );

  const showingLabel =
    viewMode === "tillnow"
      ? "Till Now"
      : viewMode === "fy"
        ? `FY ${selectedFY}`
        : getMonthLabel(selectedMonth);

  return (
    <div style={styles.page}>
      <div style={styles.headerCard}>
        <div style={styles.headerTop}>
          <div>
            <h2 style={styles.title}>Employee Drawing Report</h2>
            <p style={styles.subtitle}>
              Project wise work type, drawing/document count, employee hours and
              employee rate wise cost.
            </p>
          </div>

          <button onClick={fetchData} style={styles.refreshBtn}>
            ↻ Refresh
          </button>
        </div>

        <div style={styles.filterGrid}>
          <div>
            <label style={styles.label}>Select Project</label>

            <Select
              options={projectSelectOptions}
              value={selectedProjectOption}
              onChange={(option) =>
                setSelectedProject(option ? option.value : "")
              }
              isSearchable
              isClearable
              placeholder="Search Project..."
              noOptionsMessage={() => "No project found"}
              styles={selectStyles}
            />
          </div>

          <div>
            <label style={styles.label}>Report View</label>
            <div style={styles.tabWrap}>
              <button
                onClick={() => setViewMode("tillnow")}
                style={
                  viewMode === "tillnow" ? styles.activeTab : styles.inactiveTab
                }
              >
                Till Now
              </button>

              <button
                onClick={() => setViewMode("fy")}
                style={
                  viewMode === "fy" ? styles.activeTab : styles.inactiveTab
                }
              >
                FY Year
              </button>

              <button
                onClick={() => setViewMode("month")}
                style={
                  viewMode === "month" ? styles.activeTab : styles.inactiveTab
                }
              >
                Month Wise
              </button>
            </div>
          </div>

          {viewMode === "fy" && (
            <div>
              <label style={styles.label}>Select FY Year</label>
              <select
                value={selectedFY}
                onChange={(e) => setSelectedFY(e.target.value)}
                style={styles.select}
              >
                {fyOptions.map((fy) => (
                  <option key={fy} value={fy}>
                    FY {fy}
                  </option>
                ))}
              </select>
            </div>
          )}

          {viewMode === "month" && (
            <div>
              <label style={styles.label}>Select Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                style={styles.select}
              >
                {monthOptions.map((month) => (
                  <option key={month} value={month}>
                    {getMonthLabel(month)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div style={styles.badgeWrap}>
          <Badge text={`Showing: ${showingLabel}`} />
          <Badge text={`Work Types: ${workTypeReport.length}`} />
          <Badge text={`Employees: ${employeeNames.length}`} />
          <Badge text={`Estimated: ${totalEstimated.toFixed(2)} hrs`} />
          <Badge text={`Actual: ${totalActual.toFixed(2)} hrs`} />
          <Badge
            text={`Diff: ${(totalEstimated - totalActual).toFixed(2)} hrs`}
            bg={totalEstimated - totalActual < 0 ? "#fee2e2" : "#dcfce7"}
            color={totalEstimated - totalActual < 0 ? "#b91c1c" : "#166534"}
          />
          <Badge text={`Cost: ₹${totalCost.toFixed(2)}`} />
        </div>
      </div>

      {loading ? (
        <div style={styles.loading}>Loading report...</div>
      ) : (
        <ReportTable
          rows={workTypeReport}
          employeeNames={employeeNames}
          empRates={empRates}
          normalizeName={normalizeName}
        />
      )}
    </div>
  );
}

function ReportTable({ rows, employeeNames, empRates, normalizeName }) {
  const grandEstimated = rows.reduce((s, r) => s + r.estimatedTotal, 0);
  const grandActual = rows.reduce((s, r) => s + r.actualTotal, 0);
  const grandCost = rows.reduce((s, r) => s + r.costTotal, 0);
  const grandUniqueDocs = rows.reduce((s, r) => s + r.uniqueDrawingCount, 0);
  const grandTotalDocs = rows.reduce((s, r) => s + r.totalDocs, 0);

  const employeeGrandTotals = {};
  employeeNames.forEach((name) => {
    employeeGrandTotals[name] = rows.reduce(
      (sum, row) => sum + Number(row.employees?.[name] || 0),
      0,
    );
  });

  return (
    <div style={styles.tableCard}>
      <h3 style={styles.tableTitle}>
        Work Type Wise Drawing / Document Employee Hours
      </h3>

      <div style={styles.tableWrap}>
        <table
          style={{
            ...styles.table,
            minWidth: Math.max(1200, 780 + employeeNames.length * 145),
          }}
        >
          <thead>
            <tr>
              <th style={styles.thLeft}>Work Type / Drawing Document</th>
              <th style={styles.th}>Unique Docs</th>
              <th style={styles.th}>Total Docs</th>
              <th style={styles.th}>Estimated</th>
              <th style={styles.th}>Actual</th>
              <th style={styles.th}>Diff</th>
              <th style={styles.th}>Cost</th>

              {employeeNames.map((name) => (
                <th key={name} style={styles.th}>
                  <div>{name}</div>
                  <small>
                    ₹{Number(empRates[normalizeName(name)] || 0)}/hr
                  </small>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7 + employeeNames.length} style={styles.noData}>
                  No data found
                </td>
              </tr>
            ) : (
              rows.map((work) => (
                <React.Fragment key={work.workType}>
                  <tr style={styles.workRow}>
                    <td style={styles.workTd}>🧰 {work.workType}</td>
                    <td style={styles.workCenter}>{work.uniqueDrawingCount}</td>
                    <td style={styles.workCenter}>{work.totalDocs}</td>
                    <td style={styles.workCenter}>
                      {work.estimatedTotal.toFixed(2)}
                    </td>
                    <td style={styles.workCenter}>
                      {work.actualTotal.toFixed(2)}
                    </td>
                    <td
                      style={{
                        ...styles.workCenter,
                        color:
                          work.estimatedTotal - work.actualTotal < 0
                            ? "#dc2626"
                            : "#16a34a",
                      }}
                    >
                      {(work.estimatedTotal - work.actualTotal).toFixed(2)}
                    </td>
                    <td style={styles.workCenter}>
                      ₹{work.costTotal.toFixed(2)}
                    </td>

                    {employeeNames.map((name) => (
                      <td key={name} style={styles.workCenter}>
                        {work.employees?.[name]
                          ? Number(work.employees[name]).toFixed(2)
                          : "0.00"}
                      </td>
                    ))}
                  </tr>

                  {work.drawings.map((doc) => (
                    <tr key={`${work.workType}-${doc.name}`}>
                      <td style={styles.docTd}>📄 {doc.name}</td>
                      <td style={styles.centerTd}>1</td>
                      <td style={styles.centerTd}>1</td>
                      <td style={styles.centerTd}>
                        {doc.estimatedTotal.toFixed(2)}
                      </td>
                      <td style={styles.centerTd}>
                        {doc.actualTotal.toFixed(2)}
                      </td>
                      <td
                        style={{
                          ...styles.centerTd,
                          fontWeight: "bold",
                          color:
                            doc.estimatedTotal - doc.actualTotal < 0
                              ? "#dc2626"
                              : "#16a34a",
                        }}
                      >
                        {(doc.estimatedTotal - doc.actualTotal).toFixed(2)}
                      </td>
                      <td style={styles.centerTd}>
                        ₹{doc.costTotal.toFixed(2)}
                      </td>

                      {employeeNames.map((name) => (
                        <td key={name} style={styles.centerTd}>
                          {doc.employees?.[name]
                            ? Number(doc.employees[name]).toFixed(2)
                            : ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))
            )}

            {rows.length > 0 && (
              <tr style={styles.totalRow}>
                <td style={styles.totalTd}>Grand Total</td>
                <td style={styles.totalCenter}>{grandUniqueDocs}</td>
                <td style={styles.totalCenter}>{grandTotalDocs}</td>
                <td style={styles.totalCenter}>{grandEstimated.toFixed(2)}</td>
                <td style={styles.totalCenter}>{grandActual.toFixed(2)}</td>
                <td
                  style={{
                    ...styles.totalCenter,
                    color:
                      grandEstimated - grandActual < 0 ? "#dc2626" : "#16a34a",
                  }}
                >
                  {(grandEstimated - grandActual).toFixed(2)}
                </td>
                <td style={styles.totalCenter}>₹{grandCost.toFixed(2)}</td>

                {employeeNames.map((name) => (
                  <td key={name} style={styles.totalCenter}>
                    {employeeGrandTotals[name]
                      ? employeeGrandTotals[name].toFixed(2)
                      : "0.00"}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Badge({ text, bg = "#eef2ff", color = "#343A76" }) {
  return (
    <div
      style={{
        background: bg,
        color,
        padding: "9px 14px",
        borderRadius: 999,
        fontWeight: 700,
        fontSize: 13,
        border: "1px solid rgba(52,58,118,0.12)",
      }}
    >
      {text}
    </div>
  );
}

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 45,
    borderRadius: 10,
    borderColor: state.isFocused ? "#343A76" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 1px #343A76" : "none",
    fontSize: 14,
    cursor: "pointer",
    "&:hover": {
      borderColor: "#343A76",
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: "#6b7280",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#111827",
    fontWeight: 600,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#343A76"
      : state.isFocused
        ? "#eef2ff"
        : "#fff",
    color: state.isSelected ? "#fff" : "#111827",
    cursor: "pointer",
    fontWeight: state.isSelected ? 700 : 500,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: 10,
    overflow: "hidden",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

const styles = {
  page: {
    padding: 20,
    background: "#f5f7fb",
    minHeight: "100vh",
  },
  headerCard: {
    background: "#fff",
    borderRadius: 18,
    padding: 20,
    boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
    border: "1px solid #e5e7eb",
    marginBottom: 18,
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 18,
  },
  title: {
    color: "#343A76",
    margin: 0,
    fontSize: 26,
    fontWeight: 800,
  },
  subtitle: {
    margin: "6px 0 0",
    color: "#6b7280",
    fontSize: 14,
  },
  refreshBtn: {
    background: "#343A76",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "10px 16px",
    cursor: "pointer",
    fontWeight: 700,
  },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 16,
    alignItems: "end",
  },
  label: {
    display: "block",
    fontWeight: 800,
    color: "#111827",
    marginBottom: 8,
    fontSize: 13,
  },
  select: {
    width: "100%",
    padding: "11px 12px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    background: "#fff",
    fontSize: 14,
    outline: "none",
    color: "#111827",
  },
  tabWrap: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  activeTab: {
    padding: "11px 16px",
    border: "none",
    borderRadius: 999,
    cursor: "pointer",
    background: "#343A76",
    color: "#fff",
    fontWeight: 800,
  },
  inactiveTab: {
    padding: "11px 16px",
    border: "1px solid #e5e7eb",
    borderRadius: 999,
    cursor: "pointer",
    background: "#fff",
    color: "#111827",
    fontWeight: 800,
  },
  badgeWrap: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 18,
  },
  loading: {
    background: "#fff",
    borderRadius: 14,
    padding: 20,
    color: "#343A76",
    fontWeight: 700,
  },
  tableCard: {
    background: "#fff",
    borderRadius: 18,
    padding: 16,
    boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
    border: "1px solid #e5e7eb",
  },
  tableTitle: {
    color: "#343A76",
    margin: "0 0 12px",
    fontSize: 18,
    fontWeight: 800,
  },
  tableWrap: {
    overflowX: "auto",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    background: "#fff",
    fontSize: 14,
  },
  th: {
    background: "#343A76",
    color: "#fff",
    padding: "12px 10px",
    textAlign: "center",
    whiteSpace: "nowrap",
    fontSize: 13,
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  thLeft: {
    background: "#343A76",
    color: "#fff",
    padding: "12px 12px",
    textAlign: "left",
    whiteSpace: "nowrap",
    fontSize: 13,
    position: "sticky",
    top: 0,
    left: 0,
    zIndex: 3,
    minWidth: 320,
  },
  workRow: {
    background: "#eef2ff",
    fontWeight: 800,
  },
  workTd: {
    padding: "12px 12px",
    borderBottom: "1px solid #dbe2ff",
    color: "#111827",
    position: "sticky",
    left: 0,
    background: "#eef2ff",
    zIndex: 1,
    minWidth: 320,
  },
  workCenter: {
    padding: "12px 10px",
    borderBottom: "1px solid #dbe2ff",
    textAlign: "center",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  docTd: {
    padding: "11px 12px 11px 34px",
    borderBottom: "1px solid #edf0f5",
    color: "#003087",
    fontWeight: 700,
    minWidth: 320,
    position: "sticky",
    left: 0,
    background: "#fff",
    zIndex: 1,
  },
  centerTd: {
    padding: "11px 10px",
    borderBottom: "1px solid #edf0f5",
    textAlign: "center",
    whiteSpace: "nowrap",
    color: "#111827",
  },
  totalRow: {
    background: "#fef3c7",
    fontWeight: 900,
  },
  totalTd: {
    padding: "13px 12px",
    borderTop: "2px solid #343A76",
    color: "#111827",
    position: "sticky",
    left: 0,
    background: "#fef3c7",
    zIndex: 1,
  },
  totalCenter: {
    padding: "13px 10px",
    borderTop: "2px solid #343A76",
    textAlign: "center",
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  noData: {
    padding: 20,
    textAlign: "center",
    color: "#6b7280",
    fontWeight: 700,
  },
};

export default EmployeeDrawingReport;
